import React from "react";
import styled from "styled-components";
import { Link } from "gatsby";

const StyledSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 120px;

  .top {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 24px;
    align-items: end;
    margin-bottom: 30px;

    @media (max-width: 760px) {
      grid-template-columns: 1fr;
    }
  }

  .eyebrow {
    margin: 0 0 10px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .title {
    margin: 0;
    color: var(--text-primary);
    font-size: clamp(34px, 4.6vw, 56px);
    line-height: 1.06;
    letter-spacing: -0.045em;
    max-width: 760px;
  }

  .browse {
    color: var(--text-primary);
    text-decoration: none;
    font-family: var(--font-mono);
    font-size: 13px;
    border-bottom: 1px solid var(--border-medium);
    padding-bottom: 4px;
  }

  .grid {
    border-top: 1px solid var(--border-light);
  }

  .item {
    display: grid;
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.45fr) auto;
    gap: 18px;
    align-items: start;
    padding: 24px 0;
    border-bottom: 1px solid var(--border-light);
    text-decoration: none;
    color: inherit;
    transition: var(--transition);

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
      gap: 10px;
    }
  }

  .item:hover {
    transform: translateY(-1px);
  }

  .roleTitle {
    margin: 0;
    color: var(--text-primary);
    font-size: clamp(26px, 3vw, 34px);
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .desc {
    margin: 0;
    max-width: 62ch;
    color: var(--text-secondary);
    font-size: 15px;
    line-height: 1.85;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-end;
    align-items: center;

    @media (max-width: 900px) {
      justify-content: flex-start;
    }
  }

  .tag {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  .action {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-primary);
    border-bottom: 1px solid var(--border-medium);
    padding-bottom: 3px;
  }
`;

const roles = [
  {
    title: "Data Analyst",
    to: "/roles/data-analyst/",
    desc: "Reporting, KPI design, forecasting, dashboards, and decision-ready insights across operational and business datasets.",
    tags: ["Python", "SQL", "Dashboards"],
  },
  {
    title: "Data Scientist",
    to: "/roles/data-scientist/",
    desc: "Applied machine learning, predictive modeling, NLP, and computer vision with stronger focus on validity, baselines, and evaluation.",
    tags: ["ML", "Modeling", "Evaluation"],
  },
  {
    title: "Machine Learning Engineer",
    to: "/roles/machine-learning-engineer/",
    desc: "End-to-end ML work from feature preparation to evaluation and deployment-friendly outputs, with reproducible logic and reliable metrics.",
    tags: ["Pipelines", "Features", "Shipping"],
  },
];

const RolePaths = () => {
  return (
    <StyledSection>
      <div className="top">
        <div>
          <p className="eyebrow">Role Paths</p>
          <h2 className="title">Choose the lens that best matches the role.</h2>
        </div>

        <Link className="browse" to="/roles">
          View all role pages
        </Link>
      </div>

      <div className="grid">
        {roles.map((role) => (
          <Link key={role.title} className="item" to={role.to}>
            <h3 className="roleTitle">{role.title}</h3>

            <p className="desc">{role.desc}</p>

            <div className="meta">
              {role.tags.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
              <span className="action">Open page</span>
            </div>
          </Link>
        ))}
      </div>
    </StyledSection>
  );
};

export default RolePaths;